import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { supabase } from "./supabase";

interface StoreRepRow {
  "Store id": string;
  "Store name": string;
  Agency: string;
  Territory: string;
  "Assigned Rep": string;
}

interface StoreWithTerritories {
  reference_number: string | null;
  name: string;
  stores_territories: {
    territory: {
      name: string;
      agencies: { name: string } | null;
      users: {
        full_name: string;
        deleted_at: string | null;
      } | null;
    };
  }[];
}

export async function downloadRepAssignments(): Promise<void> {
  const { data, error } = await supabase
    .from("stores")
    .select(
      "reference_number, name, stores_territories(territory:territories(name, agencies(name), users(full_name, deleted_at)))",
    )
    .order("reference_number", { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  const stores = data as unknown as StoreWithTerritories[];

  const rows: StoreRepRow[] = [];

  for (const store of stores) {
    const territories = store.stores_territories ?? [];

    if (territories.length === 0) {
      // Store has no territory assignments
      rows.push({
        "Store id": store.reference_number ?? "",
        "Store name": store.name,
        Agency: "",
        Territory: "",
        "Assigned Rep": "",
      });
    } else {
      // One row per territory assignment
      for (const st of territories) {
        const territory = st.territory;
        const user = territory?.users;

        let repName = "";
        if (user) {
          repName = user.deleted_at
            ? `${user.full_name} (deleted)`
            : user.full_name;
        }

        rows.push({
          "Store id": store.reference_number ?? "",
          "Store name": store.name,
          Agency: territory?.agencies?.name ?? "",
          Territory: territory?.name ?? "",
          "Assigned Rep": repName,
        });
      }
    }
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Rep Assignments");

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const today = new Date().toISOString().slice(0, 10);
  saveAs(blob, `store-rep-assignments-${today}.xlsx`);
}
